import * as THREE from 'three';
import { type GroundKind, groundTexture } from './textures';

export interface TerrainSource {
  minX: number;
  minZ: number;
  width: number;
  depth: number;
  heightAt: (x: number, z: number) => number;
  /** Weights for the four ground layers (need not be normalised). */
  surfaceAt: (x: number, z: number) => [number, number, number, number];
  ground: [GroundKind, GroundKind, GroundKind, GroundKind];
  /** Metres per texture repeat for each layer. */
  scale?: [number, number, number, number];
}

const CHUNK = 32;
const RES = 1; // metres between vertices

/**
 * Heightfield terrain split into chunks for frustum culling. Splat weights are
 * stored per vertex and blended in the fragment shader with a world-space
 * macro-variation noise so large surfaces never look tiled.
 */
export class Terrain {
  readonly group = new THREE.Group();
  readonly material: THREE.MeshStandardMaterial;

  constructor(src: TerrainSource) {
    const tex = src.ground.map((g) => groundTexture(g));
    const scale = src.scale ?? [4, 5, 7, 5];
    this.material = new THREE.MeshStandardMaterial({ roughness: 0.95, metalness: 0 });
    const uniforms = {
      tLayer0: { value: tex[0] },
      tLayer1: { value: tex[1] },
      tLayer2: { value: tex[2] },
      tLayer3: { value: tex[3] },
      uScale: { value: new THREE.Vector4(1 / scale[0], 1 / scale[1], 1 / scale[2], 1 / scale[3]) },
    };
    this.material.onBeforeCompile = (shader) => {
      Object.assign(shader.uniforms, uniforms);
      shader.vertexShader = shader.vertexShader
        .replace(
          '#include <common>',
          `#include <common>
          attribute vec4 splat;
          varying vec4 vSplat;
          varying vec3 vWorldPos;`,
        )
        .replace(
          '#include <begin_vertex>',
          `#include <begin_vertex>
          vSplat = splat;
          vWorldPos = (modelMatrix * vec4(position, 1.0)).xyz;`,
        );
      shader.fragmentShader = shader.fragmentShader
        .replace(
          '#include <common>',
          `#include <common>
          uniform sampler2D tLayer0, tLayer1, tLayer2, tLayer3;
          uniform vec4 uScale;
          varying vec4 vSplat;
          varying vec3 vWorldPos;
          float tHash(vec2 p){ return fract(sin(dot(p, vec2(127.1, 311.7))) * 43758.5453); }
          float tNoise(vec2 p){
            vec2 i = floor(p); vec2 f = fract(p); vec2 u = f*f*(3.0-2.0*f);
            return mix(mix(tHash(i), tHash(i+vec2(1,0)), u.x), mix(tHash(i+vec2(0,1)), tHash(i+vec2(1,1)), u.x), u.y);
          }`,
        )
        .replace(
          '#include <map_fragment>',
          `
          vec2 wp = vWorldPos.xz;
          float macro = tNoise(wp * 0.035) * 0.6 + tNoise(wp * 0.11) * 0.4;
          // Break up layer borders with noise so transitions look organic.
          vec4 w = vSplat + (vec4(tNoise(wp*0.35), tNoise(wp*0.35+17.0), tNoise(wp*0.35+31.0), tNoise(wp*0.35+47.0)) - 0.5) * 0.35 * step(0.02, vSplat);
          w = max(w, 0.0);
          // Height-style sharpening: favour the dominant layer.
          w = pow(w, vec4(2.2));
          w /= max(1e-4, w.x + w.y + w.z + w.w);
          // Anti-tiling: blend two samples at different scales/offsets by a noise mask.
          float at = smoothstep(0.3, 0.7, tNoise(wp * 0.06 + 3.1));
          mat2 rotA = mat2(0.8, -0.6, 0.6, 0.8);
          vec3 c0 = mix(texture2D(tLayer0, wp * uScale.x).rgb, texture2D(tLayer0, rotA * wp * uScale.x * 0.37 + 0.31).rgb, at);
          vec3 c1 = mix(texture2D(tLayer1, wp * uScale.y).rgb, texture2D(tLayer1, rotA * wp * uScale.y * 0.41 + 0.57).rgb, at);
          vec3 c2 = mix(texture2D(tLayer2, wp * uScale.z).rgb, texture2D(tLayer2, rotA * wp * uScale.z * 0.43 + 0.13).rgb, at);
          vec3 c3 = mix(texture2D(tLayer3, wp * uScale.w).rgb, texture2D(tLayer3, rotA * wp * uScale.w * 0.39 + 0.71).rgb, at);
          vec3 terr = c0 * w.x + c1 * w.y + c2 * w.z + c3 * w.w;
          terr *= mix(0.82, 1.12, macro);
          diffuseColor.rgb *= terr;
          `,
        );
    };
    this.material.customProgramCacheKey = () => 'terrain-splat';

    const cols = Math.ceil(src.width / CHUNK);
    const rows = Math.ceil(src.depth / CHUNK);
    for (let cz = 0; cz < rows; cz++) {
      for (let cx = 0; cx < cols; cx++) {
        const x0 = src.minX + cx * CHUNK;
        const z0 = src.minZ + cz * CHUNK;
        const w = Math.min(CHUNK, src.minX + src.width - x0);
        const d = Math.min(CHUNK, src.minZ + src.depth - z0);
        const mesh = new THREE.Mesh(this.buildChunk(src, x0, z0, w, d), this.material);
        mesh.receiveShadow = true;
        mesh.name = `terrain_${cx}_${cz}`;
        mesh.matrixAutoUpdate = false;
        mesh.updateMatrix();
        this.group.add(mesh);
      }
    }
  }

  private buildChunk(src: TerrainSource, x0: number, z0: number, w: number, d: number): THREE.BufferGeometry {
    const nx = Math.max(1, Math.round(w / RES));
    const nz = Math.max(1, Math.round(d / RES));
    const verts = (nx + 1) * (nz + 1);
    const pos = new Float32Array(verts * 3);
    const splat = new Float32Array(verts * 4);
    let i = 0;
    for (let iz = 0; iz <= nz; iz++) {
      for (let ix = 0; ix <= nx; ix++) {
        const x = x0 + (ix / nx) * w;
        const z = z0 + (iz / nz) * d;
        pos[i * 3] = x;
        pos[i * 3 + 1] = src.heightAt(x, z);
        pos[i * 3 + 2] = z;
        const s = src.surfaceAt(x, z);
        const sum = s[0] + s[1] + s[2] + s[3] || 1;
        splat[i * 4] = s[0] / sum;
        splat[i * 4 + 1] = s[1] / sum;
        splat[i * 4 + 2] = s[2] / sum;
        splat[i * 4 + 3] = s[3] / sum;
        i++;
      }
    }
    const idx: number[] = [];
    for (let iz = 0; iz < nz; iz++) {
      for (let ix = 0; ix < nx; ix++) {
        const a = iz * (nx + 1) + ix;
        const b = a + 1;
        const c = a + nx + 1;
        const e = c + 1;
        idx.push(a, c, b, b, c, e);
      }
    }
    const g = new THREE.BufferGeometry();
    g.setAttribute('position', new THREE.BufferAttribute(pos, 3));
    g.setAttribute('splat', new THREE.BufferAttribute(splat, 4));
    g.setIndex(idx);
    g.computeVertexNormals();
    g.computeBoundingSphere();
    return g;
  }

  dispose(): void {
    this.group.traverse((o) => {
      if (o instanceof THREE.Mesh) (o.geometry as THREE.BufferGeometry).dispose();
    });
    this.material.dispose();
  }
}
